/**
 * useIdleTimer – detects user inactivity and drives a warning countdown.
 *
 * How it works
 * ────────────
 * 1. While `enabled` is true, passive event listeners on `document` detect
 *    user activity (mouse, keyboard, scroll, touch).
 * 2. Each activity event resets a single `setTimeout` (the "idle timer").
 *    A 2-second throttle prevents excessive timer resets on high-frequency
 *    events like mousemove.
 * 3. When the idle timer fires (no activity for IDLE_TIMEOUT_MS), the hook
 *    transitions to warning state and starts a 1-second countdown interval.
 * 4. The consuming component can call `resetIdleTimer()` (e.g. from a
 *    "Stay Logged In" button) to cancel the warning and restart the idle
 *    timer from scratch.
 * 5. When `remainingSeconds` reaches 0, the hook keeps `isWarning: true` and
 *    `remainingSeconds: 0` — the consuming component is responsible for
 *    calling its own logout handler at that point.
 *
 * Security notes
 * ──────────────
 * • No user input is recorded — listeners only reset a timer.
 * • No data is written to any storage (localStorage, sessionStorage, cookies).
 * • All timers and listeners are cleaned up on unmount or when `enabled`
 *   becomes false.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_DURATION_S,
  ACTIVITY_THROTTLE_MS,
  ACTIVITY_EVENTS,
} from "../constants/sessionConstants";

/** Props accepted by the hook. */
export interface UseIdleTimerOptions {
  /**
   * Whether the timer should be active.
   * Pass `false` when the user is not authenticated so we don't
   * attach listeners or run timers unnecessarily.
   */
  enabled: boolean;
}

/** Values returned to the consuming component. */
export interface UseIdleTimerReturn {
  /** True when the idle timeout has fired and the warning countdown is active. */
  isWarning: boolean;
  /** Seconds remaining before auto-logout (only meaningful when isWarning is true). */
  remainingSeconds: number;
  /** Cancel the warning and restart the idle timer from scratch. */
  resetIdleTimer: () => void;
}

export function useIdleTimer({ enabled }: UseIdleTimerOptions): UseIdleTimerReturn {
  // ── Reactive state (triggers re-renders only on transitions) ──────────
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(IDLE_WARNING_DURATION_S);

  // ── Refs (mutable without re-rendering) ───────────────────────────────
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(0); // timestamp of last accepted activity event
  const isWarningRef = useRef(false); // mirror of isWarning for use inside event handlers

  // ── Helper: clear the idle setTimeout ─────────────────────────────────
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // ── Helper: clear the warning countdown interval ──────────────────────
  const clearCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // ── Start the warning countdown ───────────────────────────────────────
  const startWarning = useCallback(() => {
    isWarningRef.current = true;
    setIsWarning(true);
    setRemainingSeconds(IDLE_WARNING_DURATION_S);

    // Tick once per second.  When we hit 0 we stop the interval and
    // leave the state at { isWarning: true, remainingSeconds: 0 } so
    // the consuming component can trigger logout.
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          // Reached zero — stop counting.
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1_000);
  }, []);

  // ── Start (or restart) the idle timer ─────────────────────────────────
  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      startWarning();
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, startWarning]);

  // ── Public: cancel warning + restart idle timer ───────────────────────
  const resetIdleTimer = useCallback(() => {
    clearCountdown();
    isWarningRef.current = false;
    setIsWarning(false);
    setRemainingSeconds(IDLE_WARNING_DURATION_S);
    startIdleTimer();
  }, [clearCountdown, startIdleTimer]);

  // ── Lifecycle: attach / detach listeners and timers ───────────────────
  useEffect(() => {
    if (!enabled) {
      // Clean up everything when disabled (e.g. user logged out).
      clearIdleTimer();
      clearCountdown();
      isWarningRef.current = false;
      setIsWarning(false);
      setRemainingSeconds(IDLE_WARNING_DURATION_S);
      // Explicitly return `undefined` so TypeScript treats this as a
      // value-returning branch under `noImplicitReturns`.
      return undefined;
    }

    /**
     * Activity handler — resets the idle timer.
     *
     * Throttled: if the last accepted event was less than
     * ACTIVITY_THROTTLE_MS ago, this is a no-op.  This prevents
     * hundreds of clearTimeout/setTimeout calls per second while
     * the mouse is moving.
     *
     * Ignored during warning: once the warning dialog is visible,
     * only an explicit "Stay Logged In" click (via resetIdleTimer)
     * should cancel the countdown.  Background mouse movement while
     * the dialog is open must NOT silently reset the timer.
     */
    const onActivity = () => {
      // Don't silently reset during the warning countdown.
      if (isWarningRef.current) return;

      const now = Date.now();
      // Accept the *first* activity event immediately. After that, throttle
      // to avoid excessive timer resets during high-frequency events.
      const last = lastActivityRef.current;
      if (last > 0 && now - last < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;

      // Reset the idle timer.
      startIdleTimer();
    };

    // Attach listeners (passive: true so we never block scrolling).
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true });
    }

    // Kick off the first idle timer.
    startIdleTimer();

    // Cleanup on unmount or when `enabled` toggles.
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity);
      }
      clearIdleTimer();
      clearCountdown();
    };
  }, [enabled, clearIdleTimer, clearCountdown, startIdleTimer]);

  return { isWarning, remainingSeconds, resetIdleTimer };
}
